import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { communityService } from '../../services/api';
import ScheduleHeatmap from './ScheduleHeatmap';

/**
 * Card "Mis horarios" en la página de la comunidad. Trae mi schedule en
 * la comunidad, lo pinta con {@link ScheduleHeatmap}, y maneja los estados
 * degenerate:
 *   - comunidad sin scheduleEnabled → no renderea (devuelve null).
 *   - schedule vacío → tarjetita compacta con CTA para agregar.
 *
 * Props:
 *   communityId
 *   onEditClick: callback para abrir el editor.
 *   compact: bool — variante chica.
 *   className: extra clases en el contenedor.
 */
function WeeklyScheduleCard({ communityId, onEditClick, compact = false, className = '' }) {
  const { t } = useTranslation();

  const { data: ctx, isLoading } = useQuery({
    queryKey: ['my-schedule-context', String(communityId)],
    queryFn: async () => (await communityService.getMyScheduleContext(communityId)).data,
    enabled: !!communityId,
    staleTime: 30_000,
  });

  const enabled = !!ctx?.scheduleEnabled;
  const days = ctx?.schedule?.days || [];
  const sedes = ctx?.sedes || [];
  const totalRanges = days.reduce((acc, d) => acc + (d?.length || 0), 0);

  const padX = compact ? 'p-4' : 'p-5';

  // Skeleton mientras carga.
  if (isLoading) {
    return (
      <section className={`bg-surface-container rounded-2xl ${padX} ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-[20px] text-primary">schedule</span>
          <h2 className={`font-bold tracking-tight text-on-surface ${compact ? 'text-base' : 'text-lg'}`}>
            {t('Mis horarios')}
          </h2>
        </div>
        <div className="h-32 rounded-lg bg-surface-container-high/30 animate-pulse" />
      </section>
    );
  }

  // Comunidad sin horarios habilitados → no se renderea (que el padre tampoco
  // reserve espacio).
  if (!enabled) {
    return null;
  }

  // Sin horarios cargados → card con contenido centrado y grilla fantasma
  // como pattern de fondo (full-width, baja opacidad) para dar peso visual
  // sin romper el centrado.
  if (totalRanges === 0) {
    return (
      <section
        className={`bg-surface-container rounded-2xl px-6 py-8 ${className}`}
      >
        <button
          type="button"
          onClick={onEditClick}
          className="w-full flex flex-col items-center text-center gap-3 group"
        >
          <span className="w-14 h-14 rounded-2xl bg-primary-container flex items-center justify-center shrink-0 shadow-sm">
            <span className="material-symbols-outlined text-[28px] text-primary">schedule</span>
          </span>
          <span className="block text-lg font-bold text-on-surface">
            {t('Agregar mis horarios')}
          </span>
          <span className="block text-sm text-on-surface-variant leading-snug max-w-[36ch]">
            {t('Decí en qué días y horarios estás disponible en cada sede')}
          </span>
          <span className="mt-1 inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-bold group-hover:opacity-90 transition-opacity">
            {t('Configurar')}
            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </span>
        </button>
      </section>
    );
  }

  // Render principal.
  return (
    <section className={`bg-surface-container rounded-2xl ${padX} ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className={`font-bold tracking-tight text-on-surface flex items-center gap-2 ${compact ? 'text-base' : 'text-lg'}`}>
          <span className="material-symbols-outlined text-[20px] text-primary">schedule</span>
          {t('Mis horarios')}
        </h2>
        {onEditClick && (
          <button
            type="button"
            onClick={onEditClick}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
          >
            <span className="material-symbols-outlined text-[14px]">edit</span>
            {t('Editar')}
          </button>
        )}
      </div>
      <ScheduleHeatmap days={days} sedes={sedes} compact={compact} />
    </section>
  );
}

export default WeeklyScheduleCard;
